/**
 * RelatedItemsPanel
 * ---------------------------------------------------------------------------
 * Compact secondary panel that surfaces customer / vehicle / invoice / booking
 * / job-card hits matching the current page-level search term. Lets the SM
 * see "everything related to this phone number / rego" in one glance while
 * the customer is on the phone — without leaving the page they're on.
 *
 * Each hit renders as a click-through row that opens in a NEW TAB so the SM
 * keeps the current Bookings / Job Cards page intact for context.
 *
 * Designed to be rendered below the primary kanban / list when the search
 * box has content and at least one related hit exists outside the primary
 * scope of the page (i.e. on Bookings, the JCs / invoices / customers are
 * "related"; on JobCards, the bookings / invoices / customers are).
 */
import { useMemo } from 'react';
import { withSessionHash } from '@/lib/authFetch';
import {
  User as UserIcon, Car, Receipt, ClipboardList, Calendar, ExternalLink,
} from 'lucide-react';

const TYPE_ICON = {
  customer: UserIcon,
  vehicle: Car,
  invoice: Receipt,
  job_card: ClipboardList,
  booking: Calendar,
};
const TYPE_LABEL = {
  customer: 'Customers',
  vehicle: 'Vehicles',
  invoice: 'Invoices',
  job_card: 'Job Cards',
  booking: 'Bookings',
};

const TYPE_COLOR = {
  customer: 'text-slate-600 bg-slate-100',
  vehicle: 'text-emerald-700 bg-emerald-50',
  invoice: 'text-violet-700 bg-violet-50',
  job_card: 'text-blue-700 bg-blue-50',
  booking: 'text-amber-700 bg-amber-50',
};

export default function RelatedItemsPanel({ results, currentTerm, primaryType }) {
  // Build the visible groups: skip the page's primary type since those are
  // already showing on the main board / list above.
  const groups = useMemo(() => {
    if (!results) return [];
    const ORDER = ['bookings', 'job_cards', 'invoices', 'customers', 'vehicles'];
    return ORDER
      .map((key) => ({
        key,
        items: results[key] || [],
      }))
      .filter((g) => {
        // Skip the type the parent page already renders.
        if (primaryType === 'job_cards' && g.key === 'job_cards') return false;
        if (primaryType === 'bookings' && g.key === 'bookings') return false;
        return g.items.length > 0;
      });
  }, [results, primaryType]);

  if (!currentTerm || groups.length === 0) return null;

  const totalHits = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div
      className="mt-6 border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
      data-testid="related-items-panel"
    >
      <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            Related to "{currentTerm}"
          </p>
          <p className="text-sm text-slate-700">
            {totalHits} related record{totalHits === 1 ? '' : 's'} — click to open in new tab
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100">
        {groups.map((g) => {
          const singularKey = g.key.replace(/s$/, '');
          const Icon = TYPE_ICON[singularKey] || ExternalLink;
          const color = TYPE_COLOR[singularKey] || 'text-slate-600 bg-slate-100';
          return (
            <div
              key={g.key}
              className="bg-white p-3"
              data-testid={`related-group-${g.key}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${color}`}>
                  <Icon className="h-3 w-3" />
                  {TYPE_LABEL[singularKey] || g.key}
                </span>
                <span className="text-[11px] text-slate-400">{g.items.length}</span>
              </div>
              <div className="space-y-1.5">
                {g.items.map((r) => (
                  <a
                    key={`${r.type}-${r.id}`}
                    href={withSessionHash(r.href)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-start justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 group"
                    data-testid={`related-${r.type}-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate group-hover:text-blue-700">
                        {r.title}
                      </p>
                      {r.subtitle && (
                        <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.tag && (
                        <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                          {r.tag}
                        </span>
                      )}
                      <ExternalLink className="h-3 w-3 text-slate-300 group-hover:text-blue-500" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
